import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, Signal, WritableSignal, inject, signal } from '@angular/core';

import { RouterOutlet } from '@angular/router';

import { DsSpinner } from '@gde-mit/angular-design-system';

import { buildLoginAppUrl } from '@shared/auth/login-app-url';
import { LoginRedirectService } from '@shared/auth/login-redirect.service';

import { ChangeEventsService } from '@shared/services/change-events/change-events.service';
import { UserService } from '@shared/services/user.service';

import { TranslocoDirective } from '@jsverse/transloco';

import { AppSettingsService } from '../app-settings.service';
import { SHARED_TRANSLOCO } from './app.routes';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TranslocoDirective, DsSpinner],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  private readonly userService = inject(UserService);
  private readonly appSettingsService = inject(AppSettingsService);
  private readonly loginRedirect = inject(LoginRedirectService);
  private readonly changeEvents = inject(ChangeEventsService);

  readonly authState: WritableSignal<'error' | 'loading' | 'ok' | 'unauthorized'> = signal('loading');

  readonly profileCheckPending: Signal<boolean> = this.userService.profileCheckPending;
  readonly SHARED_TRANSLOCO_SCOPE = SHARED_TRANSLOCO.scope;

  ngOnInit(): void {
    this.authState.set('loading');
    void this.userService
      .getMe()
      .then((user) => {
        if (!user) {
          throw new Error('No user data.');
        }
        this.changeEvents.start();
        this.authState.set('ok');
      })
      .catch((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          try {
            this.loginRedirect.redirectTo(this.getLoginUrl());
          } catch {
            this.authState.set('unauthorized');
          }
          return;
        }

        this.authState.set('error');
      });
  }

  getLoginUrl(): string {
    const loginUrl = this.appSettingsService.appConfig?.LOGIN_APP_FE_URL?.trim();
    if (!loginUrl) {
      this.authState.set('error');
      throw new Error('Login URL is not set.');
    }
    return buildLoginAppUrl(loginUrl, window.location.href, this.appSettingsService.appConfig?.MTP_APPLICATION_CODE);
  }
}
